/**
 * Web shim for `@sbaiahmed1/react-native-biometrics`.
 *
 * The real library loads a native TurboModule at import-time which crashes under
 * react-native-web/webpack. On web we don't support biometrics, so we export a
 * compatible API that returns "unsupported" results instead of throwing.
 */

export enum BiometricStrength {
  Strong = 'strong',
  Weak = 'weak',
}

export type BiometricSensorInfo = {
  available: boolean;
  biometryType?: 'Biometrics' | 'FaceID' | 'TouchID' | 'None' | 'Unknown';
  error?: string;
  errorCode?: string;
  fallbackUsed?: boolean;
  biometricStrength?: BiometricStrength;
};

export type BiometricAuthOptions = {
  title?: string;
  subtitle?: string;
  description?: string;
  fallbackLabel?: string;
  cancelLabel?: string;
  disableDeviceFallback?: boolean;
  allowDeviceCredentials?: boolean;
  biometricStrength?: BiometricStrength;
};

export type BiometricAuthResult = {
  success: boolean;
  error?: string;
  errorCode?: string;
  fallbackUsed?: boolean;
  biometricStrength?: BiometricStrength;
};

export type KeyCreationResult = { publicKey: string };
export type KeyDeletionResult = { success: boolean };

export type KeyIntegrityResult = {
  valid: boolean;
  keyExists: boolean;
  keyAttributes?: {
    algorithm: string;
    keySize: number;
    creationDate?: string;
    securityLevel: string;
  };
  integrityChecks: {
    keyFormatValid: boolean;
    keyAccessible: boolean;
    signatureTestPassed: boolean;
    hardwareBacked: boolean;
  };
  error?: string;
};

export type SignatureResult = {
  success: boolean;
  signature?: string;
  error?: string;
  errorCode?: string;
};

export enum InputEncoding {
  UTF8 = 'utf8',
  Base64 = 'base64',
}

export type SignatureOptions = {
  keyAlias?: string;
  data: string;
  inputEncoding?: InputEncoding;
  promptTitle?: string;
  promptSubtitle?: string;
  cancelButtonText?: string;
  biometricStrength?: BiometricStrength;
  disableDeviceFallback?: boolean;
};

export type SignatureValidationResult = { valid: boolean; error?: string };

export type KeyAttributesResult = {
  exists: boolean;
  attributes?: {
    algorithm: string;
    keySize: number;
    purposes: string[];
    digests: string[];
    padding: string[];
    creationDate?: string;
    securityLevel: string;
    hardwareBacked: boolean;
    userAuthenticationRequired: boolean;
  };
  error?: string;
};

export type GetAllKeysResult = {
  keys: Array<{
    alias: string;
    publicKey: string;
  }>;
};

export type DiagnosticInfo = {
  platform: string;
  osVersion: string;
  deviceModel: string;
  biometricCapabilities: string[];
  securityLevel: string;
  keyguardSecure: boolean;
  enrolledBiometrics: string[];
  lastError?: string;
};

export type BiometricTestResult = {
  success: boolean;
  results: {
    sensorAvailable: boolean;
    canAuthenticate: boolean;
    hardwareDetected: boolean;
    hasEnrolledBiometrics: boolean;
    secureHardware: boolean;
  };
  errors: string[];
  warnings: string[];
};

export type DeviceIntegrityResult = {
  isRooted?: boolean;
  isJailbroken?: boolean;
  isKeyguardSecure?: boolean;
  hasSecureHardware?: boolean;
  isCompromised: boolean;
  riskLevel: 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' | 'UNKNOWN';
  error?: string;
};

export type BiometricConfig = {
  keyAlias?: string;
  keyPrefix?: string;
};

export type BiometricChangeEvent = {
  timestamp: number;
  changeType:
    | 'BIOMETRIC_ENABLED'
    | 'BIOMETRIC_DISABLED'
    | 'ENROLLMENT_CHANGED'
    | 'HARDWARE_CHANGED'
    | 'STATE_CHANGED';
  biometryType: string;
  available: boolean;
  enrolled: boolean;
};

const UNSUPPORTED_ERROR = 'Biometrics are not supported on web.';
const UNSUPPORTED_CODE = 'WEB_UNSUPPORTED';

export async function isSensorAvailable(options?: {
  biometricStrength?: BiometricStrength;
}): Promise<BiometricSensorInfo> {
  return {
    available: false,
    biometryType: 'None',
    error: UNSUPPORTED_ERROR,
    errorCode: UNSUPPORTED_CODE,
    biometricStrength: options?.biometricStrength,
  };
}

export async function simplePrompt(
  _promptMessage: string,
  options?: { biometricStrength?: BiometricStrength },
): Promise<BiometricAuthResult> {
  return {
    success: false,
    error: UNSUPPORTED_ERROR,
    errorCode: UNSUPPORTED_CODE,
    biometricStrength: options?.biometricStrength,
  };
}

export async function authenticateWithOptions(
  options: BiometricAuthOptions,
): Promise<BiometricAuthResult> {
  return {
    success: false,
    error: UNSUPPORTED_ERROR,
    errorCode: UNSUPPORTED_CODE,
    biometricStrength: options?.biometricStrength,
  };
}

export async function createKeys(
  _keyAlias?: string,
  _keyType?: 'rsa2048' | 'ec256',
  _biometricStrength?: BiometricStrength,
): Promise<KeyCreationResult> {
  return { publicKey: '' };
}

export async function deleteKeys(
  _keyAlias?: string,
): Promise<KeyDeletionResult> {
  return { success: false };
}

export async function validateKeyIntegrity(
  _keyAlias?: string,
): Promise<KeyIntegrityResult> {
  return {
    valid: false,
    keyExists: false,
    integrityChecks: {
      keyFormatValid: false,
      keyAccessible: false,
      signatureTestPassed: false,
      hardwareBacked: false,
    },
    error: UNSUPPORTED_ERROR,
  };
}

export async function verifyKeySignature(
  _keyAlias: string | undefined,
  _data: string,
  _promptTitle?: string,
  _promptSubtitle?: string,
  _cancelButtonText?: string,
): Promise<SignatureResult> {
  return {
    success: false,
    error: UNSUPPORTED_ERROR,
    errorCode: UNSUPPORTED_CODE,
  };
}

export async function signWithOptions(
  _options: SignatureOptions,
): Promise<SignatureResult> {
  return {
    success: false,
    error: UNSUPPORTED_ERROR,
    errorCode: UNSUPPORTED_CODE,
  };
}

export async function validateSignature(
  _keyAlias: string | undefined,
  _data: string,
  _signature: string,
): Promise<SignatureValidationResult> {
  return { valid: false, error: UNSUPPORTED_ERROR };
}

export async function getKeyAttributes(
  _keyAlias?: string,
): Promise<KeyAttributesResult> {
  return { exists: false, error: UNSUPPORTED_ERROR };
}

export async function configureKeyAlias(_keyAlias: string): Promise<void> {
  // noop on web
}

export async function getDefaultKeyAlias(): Promise<string> {
  return '';
}

export async function getAllKeys(
  _customAlias?: string,
): Promise<GetAllKeysResult> {
  return { keys: [] };
}

export async function getDiagnosticInfo(): Promise<DiagnosticInfo> {
  return {
    platform: 'web',
    osVersion: '',
    deviceModel: '',
    biometricCapabilities: [],
    securityLevel: 'unknown',
    keyguardSecure: false,
    enrolledBiometrics: [],
    lastError: UNSUPPORTED_ERROR,
  };
}

export async function runBiometricTest(): Promise<BiometricTestResult> {
  return {
    success: false,
    results: {
      sensorAvailable: false,
      canAuthenticate: false,
      hardwareDetected: false,
      hasEnrolledBiometrics: false,
      secureHardware: false,
    },
    errors: [UNSUPPORTED_ERROR],
    warnings: [],
  };
}

export async function setDebugMode(_enabled: boolean): Promise<void> {
  // noop on web
}

export async function getDeviceIntegrityStatus(): Promise<DeviceIntegrityResult> {
  return {
    isCompromised: false,
    riskLevel: 'UNKNOWN',
    error: UNSUPPORTED_ERROR,
  };
}

export async function configure(_config: BiometricConfig): Promise<void> {
  // noop on web
}

export type LogEntry = {
  timestamp?: number;
  level?: string;
  message?: string;
  data?: unknown;
};

export const LogLevel = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
} as const;

export type LoggerConfig = Record<string, unknown>;

export const logger = {
  debug: (..._args: unknown[]) => {},
  info: (..._args: unknown[]) => {},
  warn: (..._args: unknown[]) => {},
  error: (..._args: unknown[]) => {},
};

export function enableLogging(_enabled: boolean) {}
export function setLogLevel(_level: unknown) {}
export function configureLogger(_config: LoggerConfig) {}
export function getLogs(): LogEntry[] {
  return [];
}
export function clearLogs(): void {}

export type EventSubscription = { remove: () => void };

export function subscribeToBiometricChanges(
  _callback: (event: BiometricChangeEvent) => void,
): EventSubscription {
  return { remove: () => {} };
}

export function unsubscribeFromBiometricChanges(
  _subscription: EventSubscription,
): void {}
export async function startBiometricChangeDetection(): Promise<void> {}
export async function stopBiometricChangeDetection(): Promise<void> {}
